import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

const BANNER_IMAGE =
  'https://roadtechservices.netlify.app/images/generators/generator-repair-1.jpg';
const DETAIL_IMAGE =
  'https://roadtechservices.netlify.app/images/generators/generator-repair-2.jpg';
const SIDE_IMAGE =
  'https://roadtechservices.netlify.app/images/generators/generator-repair-3.jpg';
const CTA_IMAGE =
  'https://roadtechservices.netlify.app/images/mower_banner-850w.jpeg';

const whatWeService = [
  'Portable generators',
  'Standby generators',
  'Engine diagnostics & troubleshooting',
  'Oil changes & filter replacements',
  'Carburetor cleaning & adjustment',
  'Starter & electrical repairs',
  'Routine tune-ups & load testing',
  'Spark plug & ignition service',
  'Emergency repair service',
];

const faqs = [
  {
    q: 'Do you repair all generator brands?',
    a: 'Yes, we specialize in Briggs & Stratton, Kohler, Kawasaki, Stihl, and many other leading brands.',
  },
  {
    q: 'Can you service my generator at home?',
    a: 'Absolutely. We provide mobile repair and maintenance so you never have to transport your generator.',
  },
  {
    q: 'Do you offer same-day service?',
    a: "Yes, if the issue is diagnosed correctly and required parts are available, we offer same-day repairs.",
  },
  {
    q: 'What maintenance does a generator need?',
    a: 'Regular oil changes, air filter checks, spark plug replacements, and load testing are essential for reliability.',
  },
  {
    q: 'Do you handle emergency repairs?',
    a: 'Yes, we provide urgent repair services to ensure you\'re never left without power during critical times.',
  },
];

export default function GeneratorRepair() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div className="font-body">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-24 min-h-[420px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src={BANNER_IMAGE}
            alt="Generator Repair"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/65" />
        </div>
        <div className="relative z-10 text-center px-6 max-w-3xl mx-auto">
          <span className="inline-block bg-orange-600 text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-4">
            Generators
          </span>
          <h1 className="font-display text-5xl lg:text-7xl font-bold text-white uppercase tracking-tight">
            Generator Repair & Service
          </h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="bg-white py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div className="relative h-[520px] rounded-2xl overflow-hidden lg:sticky lg:top-28">
              <img
                src={DETAIL_IMAGE}
                alt="Generator repair in Michigan"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent">
                <div className="flex items-center gap-2 text-white text-sm font-semibold">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-flashlight-line text-orange-400" />
                  </div>
                  Certified Generator Experts
                </div>
              </div>
            </div>

            <div>
              <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-4">
                Mobile Repairs • Certified • Same-Day
              </p>
              <h2 className="font-display text-4xl font-bold uppercase tracking-tight text-neutral-900 mb-5 leading-tight">
                Reliable Generator Care with Road Tech Services
              </h2>
              <p className="text-neutral-600 text-base leading-relaxed mb-4">
                At Road Tech Services, we specialize in generator repair and
                maintenance for residential clients across Michigan. Our
                certified technician is trained in Briggs &amp; Stratton,
                Kohler, Kawasaki, and Stihl power systems.
              </p>
              <p className="text-neutral-600 text-base leading-relaxed mb-8">
                From minor tune-ups to major repairs, we deliver professional,
                at-home service so you stay powered without the hassle of
                transporting your equipment. Your generator should be ready when
                you need it — we make sure it is.
              </p>

              <div className="bg-stone-50 rounded-xl p-6 mb-8">
                <p className="text-neutral-900 font-semibold text-xs mb-5 uppercase tracking-wider">
                  What We Service
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {whatWeService.map((item) => (
                    <div key={item} className="flex items-center gap-3 text-sm text-neutral-600">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-checkbox-circle-fill text-orange-500 text-base" />
                      </div>
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <p className="text-neutral-900 font-semibold text-xs mb-5 uppercase tracking-wider">
                  Our Process
                </p>
                <div className="space-y-4">
                  {[
                    'Contact us to schedule your generator service',
                    'We diagnose the issue and provide a clear estimate',
                    'Repairs are completed on-site with quality parts',
                    'We test, tune, and ensure your generator runs smoothly',
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-7 h-7 rounded-full bg-orange-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {i + 1}
                      </div>
                      <p className="text-neutral-600 text-sm leading-relaxed pt-1">{step}</p>
                    </div>
                  ))}
                </div>
              </div>

              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold px-7 py-3.5 rounded-lg transition-colors text-sm whitespace-nowrap cursor-pointer"
              >
                Schedule Generator Service
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Side Image Section */}
      <section className="bg-stone-50 py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-4">
                Power You Can Depend On
              </p>
              <h2 className="font-display text-3xl lg:text-4xl font-bold uppercase tracking-tight text-neutral-900 mb-5 leading-tight">
                Complete Generator Repair & Maintenance
              </h2>
              <p className="text-neutral-600 text-base leading-relaxed mb-6">
                Road Tech Services provides expert care for portable and standby
                generators, ensuring they&rsquo;re ready when you need them
                most — storms, outages, or emergencies.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  'Engine diagnostics & troubleshooting',
                  'Oil changes & filter replacements',
                  'Carburetor cleaning & adjustments',
                  'Starter & electrical repairs',
                  'Routine tune-ups and load testing',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3 text-sm text-neutral-600">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-arrow-right-circle-fill text-orange-500 text-base" />
                    </div>
                    {item}
                  </div>
                ))}
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white font-semibold px-7 py-3.5 rounded-lg transition-colors text-sm whitespace-nowrap cursor-pointer"
              >
                Book a Repair
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="h-[400px] rounded-2xl overflow-hidden">
              <img src={SIDE_IMAGE} alt="Generator services" className="w-full h-full object-cover object-top" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-3">FAQs</p>
            <h2 className="font-display text-3xl lg:text-4xl font-bold uppercase tracking-tight text-neutral-900">
              Generator Repair FAQs
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-stone-200 rounded-xl overflow-hidden">
                <button
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-stone-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-semibold text-neutral-900 text-sm pr-4">{faq.q}</span>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className={`${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'} text-orange-600 text-lg`} />
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <p className="text-neutral-600 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24">
        <div className="absolute inset-0">
          <img src={CTA_IMAGE} alt="Generator repair service" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-black/75" />
        </div>
        <div className="relative z-10 max-w-2xl mx-auto px-6 text-center">
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-white uppercase tracking-tight mb-5">
            Need Reliable Generator Service?
          </h2>
          <p className="text-white/75 text-base leading-relaxed mb-8">
            Road Tech Services keeps your generators running strong with
            professional repair and maintenance. Contact us today.
          </p>
          <Link to="/contact" className="inline-block bg-orange-600 hover:bg-orange-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors whitespace-nowrap cursor-pointer">
            Book a Repair
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
